import { useCallback } from 'react';
import { FlowsPages } from '@utils/enums/FlowsPages.enum';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { Patient } from '@store/patientStore';
import { getMD5, getSHA256 } from '@utils/functions';

const sendDataToTealium = (obj: Record<string, any>) => {
  if (
    window.tealium_data2track &&
    typeof window.tealium_data2track.push === 'function'
  ) {
    window.tealium_data2track.push(obj);
    console.log('[ANALYTICS - TEALIUM] push to tealium succeded', obj);
  }
};

const getStepConfig = ({
  page,
  pageCountry,
  zipCode,
  pageLanguage,
  practiceId,
  patients,
  contactsDetailsEmail,
  isEmailOptinChecked,
}: {
  page: FlowsPages;
  pageCountry: string;
  zipCode: string;
  pageLanguage: string;
  practiceId: string;
  patients: (Patient | undefined)[];
  contactsDetailsEmail?: string;
  isEmailOptinChecked?: boolean;
}) => {
  const base = {
    id: 'VirtualPage-View',
    Page_Country: pageCountry,
    Page_Language: pageLanguage,
    Exam_ZipCode: zipCode,
    Store_Search_StoreId: practiceId,
    Page_Section1: 'Exam',
  };

  //get the email trimmed and to lower case, to calculate MD5 & SHA256
  const emailProps = contactsDetailsEmail
    ? {
        Events_UserEmailSub: isEmailOptinChecked ? '1' : '0',
        User_Email_MD5: getMD5(contactsDetailsEmail),
        User_Email_SHA256: getSHA256(contactsDetailsEmail),
      }
    : {};

  const realPatientsLength = patients.filter(
    p => p !== undefined && p !== null,
  ).length;
  // at the first step of the first patient, we got length 0, but we need to send at least 1
  const realPatientsCount = Math.max(1, realPatientsLength);

  const config: Record<string, Record<string, any>> = {
    [FlowsPages.APPOINTMENT_TYPE]: {
      ...base,
      Page_Type: 'Info',
      Page_Section2: `Question${realPatientsCount}`,
    },
    [FlowsPages.SCHEDULE_EXAM]: {
      ...base,
      Page_Type: 'Info',
      Page_Section2: 'Scheduler',
    },
    [FlowsPages.PATIENT_DETAILS]: {
      ...base,
      Page_Type: 'Info',
      Page_Section2: 'PersonalInfo',
    },
    [FlowsPages.ADD_NEW_PATIENT]: {
      ...base,
      Page_Type: 'Info',
      Page_Section2: `Question${realPatientsCount + 1}`,
    },
    [FlowsPages.CONTACT_DETAILS]: {
      ...base,
      Page_Type: 'Review',
      Page_Section2: 'Review',
    },
    [FlowsPages.TYP]: {
      ...base,
      Page_Type: 'Confirmation',
      ...emailProps,
    },
    CANCELED: {
      ...base,
      Page_Type: 'Cancellation',
    },
  };

  return config[page] || base;
};

export enum AnalyticsActionType {
  ADD_PATIENT = 'ADD_PATIENT',
  CANCEL_APPOINTMENT = 'CANCEL_APPOINTMENT',
}

const useAnalytics = () => {
  const practiceId = useOnlineBookingStore(state => state.practiceId);
  const generalInfo = useOnlineBookingStore(state => state.generalInfo);
  const pageCountry = useOnlineBookingStore(state => state.pageCountry);
  const pageLanguage = useOnlineBookingStore(state => state.pageLanguage);

  const trackPageAnalytics = useCallback(
    ({
      page,
      patients,
      contactsDetailsEmail,
      isEmailOptinChecked,
    }: {
      page: FlowsPages;
      patients: (Patient | undefined)[];
      contactsDetailsEmail?: string;
      isEmailOptinChecked?: boolean;
    }) => {
      const baseAnalyticsProps = getStepConfig({
        page,
        pageCountry: pageCountry || '',
        zipCode: generalInfo?.address.postalCode || '',
        pageLanguage: pageLanguage || '',
        practiceId,
        patients,
        contactsDetailsEmail,
        isEmailOptinChecked,
      });

      let extraAnalyticsProps: Record<string, any> = {};

      switch (page) {
        case FlowsPages.APPOINTMENT_TYPE:
        case FlowsPages.PATIENT_DETAILS:
        case FlowsPages.CONTACT_DETAILS:
        case FlowsPages.SCHEDULE_EXAM:
          extraAnalyticsProps = {
            Exam_Fitting: patients
              .filter(p => p?.appointmentType)
              .map(p => p?.appointmentType?.name)
              .join(','),
          };
          break;
        case FlowsPages.TYP:
          extraAnalyticsProps = {
            Exam_Fitting: patients
              .filter(p => p?.appointmentId)
              .map(p => p?.appointmentType?.name)
              .join(','),
            Exam_PreviousExam: patients
              .filter(p => p?.appointmentId)
              .map(p => (p?.appointmentType?.eyeExamWithUsAnswer ? 'Y' : 'N'))
              .join(','),
            Exam_WornBefore: patients
              .filter(p => p?.appointmentId)
              .map(p =>
                p?.appointmentType?.clBeforeAnswer === 'cl' ? 'Y' : 'N',
              )
              .join(','),
            Exam_PatientNum: patients.filter(p => p?.appointmentId).length,
            Exam_Id: Array.from(
              new Set(
                patients
                  .filter(p => p?.appointmentId)
                  .map(p => p?.appointmentId),
              ),
            ).join(','),
          };
          break;
        default:
          extraAnalyticsProps = {};
      }

      sendDataToTealium({
        ...baseAnalyticsProps,
        ...extraAnalyticsProps,
      });
    },
    [practiceId, generalInfo, pageCountry, pageLanguage],
  );

  const trackEventAnalytics = useCallback(
    ({ action }: { action: AnalyticsActionType }) => {
      switch (action) {
        case AnalyticsActionType.ADD_PATIENT:
          sendDataToTealium({
            id: 'Click',
            Events_FunnelAddExam: '1',
          });
          break;
        case AnalyticsActionType.CANCEL_APPOINTMENT:
          sendDataToTealium({
            id: 'Event',
            Exam_PatientNum: 1,
            Events_FunnelCancellation: '1',
          });
          break;
        default:
          // No action
          break;
      }
    },
    [],
  );

  return { trackPageAnalytics, trackEventAnalytics };
};

export default useAnalytics;
